import React from "react";
import {
  ArrowRight,
  Lock,
  Crown,
  CheckCircle2,
  Rocket,
  Sparkles,
  ShieldCheck,
  BarChart3,
  Users,
  Building2,
  Star,
  PhoneCall,
  Mail,
  Zap,
  ChevronRight,
} from "lucide-react";

const features = [
  {
    title: "Advanced Analytics",
    description: "Real-time reports and business insights.",
    icon: <BarChart3 size={22} />,
  },
  {
    title: "Unlimited Users",
    description: "Add unlimited employees without restrictions.",
    icon: <Users size={22} />,
  },
  {
    title: "Multi Branch",
    description: "Manage multiple branches from one dashboard.",
    icon: <Building2 size={22} />,
  },
  {
    title: "Enterprise Security",
    description: "Bank-level encryption and security.",
    icon: <ShieldCheck size={22} />,
  },
  {
    title: "Priority Support",
    description: "Dedicated technical support team.",
    icon: <PhoneCall size={22} />,
  },
  {
    title: "AI Automation",
    description: "Automate repetitive business operations.",
    icon: <Zap size={22} />,
  },
];

const benefits = [
  "Unlimited Modules",
  "Premium Dashboard",
  "Advanced Reports",
  "Role Based Access",
  "Priority Support",
  "Future Updates Included",
];

const UpgradeRequired = () => {
  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-red-50 via-white to-red-100">

      {/* Background Blur */}
      <div className="absolute -top-32 -left-24 h-80 w-80 rounded-full bg-red-200 blur-3xl opacity-40" />
      <div className="absolute bottom-0 right-0 h-96 w-96 rounded-full bg-red-300 blur-3xl opacity-20" />

      {/* Floating Shapes */}
      <div className="absolute top-20 right-24 h-5 w-5 rounded-full bg-red-400 animate-pulse" />
      <div className="absolute bottom-40 left-24 h-3 w-3 rounded-full bg-red-500 animate-bounce" />
      <div className="absolute top-1/2 left-1/3 h-2 w-2 rounded-full bg-red-300 animate-ping" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-14">

        {/* Badge */}

        <div className="flex justify-center">

          <div className="inline-flex items-center gap-2 rounded-full border border-red-200 bg-white px-5 py-2 shadow-sm">

            <Sparkles size={16} className="text-red-600" />

            <span className="text-sm font-semibold text-red-600">
              Enterprise Module
            </span>

          </div>

        </div>

        {/* Hero */}

        <div className="mt-8 text-center">

          <div className="flex justify-center">

            <div className="flex h-24 w-24 items-center justify-center rounded-3xl bg-gradient-to-br from-red-600 to-red-500 shadow-xl">

              <Lock className="text-white" size={42} />

            </div>

          </div>

          <h1 className="mt-8 text-5xl font-bold text-gray-900">

            Upgrade Your Plan

          </h1>

          <p className="mx-auto mt-5 max-w-3xl text-lg leading-8 text-gray-600">

            Unlock this premium ERP module and access powerful enterprise
            features designed to help your business grow faster,
            automate operations and improve productivity.

          </p>

          <div className="mt-10 flex flex-wrap justify-center gap-4">

            <button className="group flex items-center gap-2 rounded-xl bg-gradient-to-r from-red-600 to-red-500 px-8 py-4 font-semibold text-white shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-2xl">

              <Rocket size={20} />

              Upgrade Now

              <ArrowRight
                size={18}
                className="transition-transform group-hover:translate-x-1"
              />

            </button>

            <button className="rounded-xl border border-red-200 bg-white px-8 py-4 font-semibold text-red-600 shadow hover:bg-red-50 transition">

              Contact Sales

            </button>

          </div>

        </div>

        {/* Stats */}

        <div className="mt-16 grid gap-6 md:grid-cols-4">

          <div className="rounded-2xl bg-white p-6 shadow-lg border border-red-100">

            <div className="text-3xl font-bold text-red-600">

              25+

            </div>

            <p className="mt-2 text-gray-600">

              Premium Modules

            </p>

          </div>

          <div className="rounded-2xl bg-white p-6 shadow-lg border border-red-100">

            <div className="text-3xl font-bold text-red-600">

              99.9%

            </div>

            <p className="mt-2 text-gray-600">

              Secure Platform

            </p>

          </div>

          <div className="rounded-2xl bg-white p-6 shadow-lg border border-red-100">

            <div className="text-3xl font-bold text-red-600">

              24/7

            </div>

            <p className="mt-2 text-gray-600">

              Premium Support

            </p>

          </div>

          <div className="rounded-2xl bg-white p-6 shadow-lg border border-red-100">

            <div className="text-3xl font-bold text-red-600">

              AI

            </div>

            <p className="mt-2 text-gray-600">

              Smart Automation

            </p>

          </div>

        </div>

                {/* Premium Features */}

        <div className="mt-20">

          <div className="text-center">

            <div className="inline-flex items-center gap-2 rounded-full bg-red-100 px-4 py-2 text-sm font-semibold text-red-600">

              <Crown size={16} />

              Premium Features

            </div>

            <h2 className="mt-5 text-4xl font-bold text-gray-900">

              Everything You Need To Scale

            </h2>

            <p className="mt-4 text-gray-600 max-w-2xl mx-auto">

              Unlock enterprise features and experience the complete APD ERP
              ecosystem with unlimited access.

            </p>

          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-2 xl:grid-cols-3">

            {features.map((feature, index) => (

              <div
                key={index}
                className="group rounded-3xl border border-red-100 bg-white p-7 shadow-md transition-all duration-300 hover:-translate-y-2 hover:border-red-300 hover:shadow-2xl"
              >

                <div className="flex items-center justify-between">

                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 text-red-600 transition-all duration-300 group-hover:bg-red-600 group-hover:text-white">

                    {feature.icon}

                  </div>

                  <Lock
                    size={18}
                    className="text-red-400"
                  />

                </div>

                <h3 className="mt-6 text-xl font-bold text-gray-900">

                  {feature.title}

                </h3>

                <p className="mt-3 leading-7 text-gray-600">

                  {feature.description}

                </p>

              </div>

            ))}

          </div>

        </div>

        {/* Upgrade Card */}

        <div className="mt-24 grid gap-10 lg:grid-cols-2">

          {/* Left */}

          <div className="rounded-3xl border border-red-100 bg-white p-10 shadow-xl">

            <div className="flex items-center gap-3">

              <div className="rounded-xl bg-red-100 p-3">

                <Star
                  size={24}
                  className="text-red-600"
                />

              </div>

              <div>

                <h3 className="text-3xl font-bold">

                  Business Plan

                </h3>

                <p className="text-gray-500">

                  Recommended for Growing Companies

                </p>

              </div>

            </div>

            <div className="mt-8">

              {benefits.map((item, index) => (

                <div
                  key={index}
                  className="mb-5 flex items-center gap-4"
                >

                  <CheckCircle2
                    size={22}
                    className="text-green-500"
                  />

                  <span className="text-gray-700 font-medium">

                    {item}

                  </span>

                </div>

              ))}

            </div>

            <div className="mt-10 rounded-2xl bg-gradient-to-r from-red-600 to-red-500 p-7 text-white">

              <div className="flex items-center justify-between">

                <div>

                  <p className="text-red-100">

                    Current Plan

                  </p>

                  <h2 className="text-3xl font-bold">

                    Starter

                  </h2>

                </div>

                <ChevronRight size={40} />

                <div className="text-right">

                  <p className="text-red-100">

                    Upgrade To

                  </p>

                  <h2 className="text-3xl font-bold">

                    Business

                  </h2>

                </div>

              </div>

            </div>

          </div>

          {/* Right */}

          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-red-600 via-red-500 to-red-700 p-10 text-white shadow-2xl">

            <div className="absolute -right-16 -top-16 h-52 w-52 rounded-full bg-white/10 blur-xl"></div>

            <div className="absolute bottom-0 left-0 h-60 w-60 rounded-full bg-black/10 blur-3xl"></div>

            <div className="relative z-10">

              <div className="inline-flex rounded-full bg-white/20 px-5 py-2 text-sm font-semibold">

                Enterprise Edition

              </div>

              <h2 className="mt-6 text-5xl font-bold leading-tight">

                Unlock
                <br />
                Premium
                <br />
                Experience

              </h2>

              <p className="mt-6 text-red-100 leading-8">

                Access every APD ERP module including HR,
                Finance, Inventory, CRM,
                Reports, AI Automation,
                Multi Branch Management
                and Priority Support.

              </p>

              <div className="mt-10 grid grid-cols-2 gap-4">

                <div className="rounded-2xl bg-white/10 p-5 backdrop-blur">

                  <h3 className="text-2xl font-bold">

                    25+

                  </h3>

                  <p className="mt-2 text-red-100">

                    Enterprise Modules

                  </p>

                </div>

                <div className="rounded-2xl bg-white/10 p-5 backdrop-blur">

                  <h3 className="text-2xl font-bold">

                    ∞

                  </h3>

                  <p className="mt-2 text-red-100">

                    Unlimited Users

                  </p>

                </div>

                <div className="rounded-2xl bg-white/10 p-5 backdrop-blur">

                  <h3 className="text-2xl font-bold">

                    AI

                  </h3>

                  <p className="mt-2 text-red-100">

                    Smart Automation

                  </p>

                </div>

                <div className="rounded-2xl bg-white/10 p-5 backdrop-blur">

                  <h3 className="text-2xl font-bold">

                    24/7

                  </h3>

                  <p className="mt-2 text-red-100">

                    Priority Support

                  </p>

                </div>

              </div>

            </div>

          </div>

        </div>

                {/* Contact Section */}

        <div className="mt-24">

          <div className="rounded-[32px] border border-red-100 bg-white p-10 shadow-xl">

            <div className="grid gap-10 lg:grid-cols-2">

              <div>

                <div className="inline-flex items-center gap-2 rounded-full bg-red-100 px-4 py-2 text-sm font-semibold text-red-600">

                  <Mail size={16} />

                  Need Help?

                </div>

                <h2 className="mt-6 text-4xl font-bold text-gray-900">

                  Let's Unlock Your Business Potential

                </h2>

                <p className="mt-5 leading-8 text-gray-600">

                  Upgrade your APD ERP subscription and gain access to every
                  premium module including HR, CRM, Inventory, Finance,
                  Marketing, Reports and AI Automation.

                </p>

                <div className="mt-8 space-y-5">

                  <div className="flex items-center gap-4">

                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-red-100">

                      <PhoneCall
                        className="text-red-600"
                        size={20}
                      />

                    </div>

                    <div>

                      <p className="text-sm text-gray-500">

                        Product Team

                      </p>

                      <h4 className="font-bold text-gray-900">

                        +91 9962376763

                      </h4>

                    </div>

                  </div>

                  <div className="flex items-center gap-4">

                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-red-100">

                      <Mail
                        className="text-red-600"
                        size={20}
                      />

                    </div>

                    <div>

                      <p className="text-sm text-gray-500">

                        Email

                      </p>

                      <h4 className="font-bold text-gray-900">

                        vigneshkumar.s@cuso.in

                      </h4>

                    </div>

                  </div>

                </div>

              </div>

              {/* CTA */}

              <div className="rounded-3xl bg-gradient-to-br from-red-600 via-red-500 to-red-700 p-10 text-white">

                <div className="flex h-full flex-col justify-between">

                  <div>

                    <div className="inline-flex rounded-full bg-white/20 px-4 py-2 text-sm font-semibold">

                      Upgrade Today

                    </div>

                    <h2 className="mt-6 text-4xl font-bold leading-tight">

                      Unlock
                      <br />
                      All Premium
                      <br />
                      Modules

                    </h2>

                    <p className="mt-6 leading-8 text-red-100">

                      Upgrade now and access every enterprise feature
                      with lifetime product updates,
                      premium customer support
                      and advanced business analytics.

                    </p>

                  </div>

                  <div className="mt-10 space-y-4">

                    <button className="flex w-full items-center justify-center gap-3 rounded-2xl bg-white py-4 font-bold text-red-600 transition hover:scale-[1.02] hover:shadow-xl">

                      <Rocket size={20} />

                      Upgrade Now

                    </button>

                    <button className="flex w-full items-center justify-center gap-3 rounded-2xl border border-white/30 py-4 font-semibold text-white transition hover:bg-white/10">

                      Contact Sales

                    </button>

                  </div>

                </div>

              </div>

            </div>

          </div>

        </div>

        {/* Footer */}

        <div className="mt-20 text-center">

          <div className="inline-flex items-center gap-2 rounded-full border border-red-100 bg-white px-6 py-3 shadow">

            <Lock
              size={18}
              className="text-red-600"
            />

            <span className="font-semibold text-gray-700">

              This module is available only in the Premium Business Plan.

            </span>

          </div>

          <p className="mt-8 text-sm text-gray-500">

            © {new Date().getFullYear()} APD ERP. All Rights Reserved.

          </p>

        </div>

      </div>

    </div>

  );

};

export default UpgradeRequired;